import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Trash2, Save, ChevronRight, X, ClipboardList, AlertTriangle, Loader2, Edit3, Check } from 'lucide-react';

/**
 * Settings → Config → Roof Conditions
 *
 * Alternative view of the same `condition_scripts` data used by the Roof
 * Appraisal flow. Pivots Roof Type → Category → Comments so the operator can:
 *   - See exactly which comments are available for a given Roof Type
 *   - Click any comment to edit its description + Roof Type membership
 *   - Delete a comment or remove a Roof Type globally from the dropdowns
 *
 * Mirrors the Comment ↔ Roof Type Matrix at /condition-script-matrix but
 * with the data laid out the same way the inspector sees it during an
 * appraisal — by Roof Type first, then by component.
 */

const CATEGORIES = [
  'Ridging', 'Tiles', 'Valleys', 'Barging', 'Spouting',
  'Sheeting', 'Fastenings', 'Flashings',
  'Roof Condition', 'Roof Recommendation', 'Appraisal Condition',
];

export default function RoofConditionsPage() {
  const { api } = useAuth();
  const [roofTypes, setRoofTypes] = useState([]);
  const [selectedRoofType, setSelectedRoofType] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Ridging');
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [busy, setBusy] = useState(null); // script id currently saving / deleting

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rt, sc] = await Promise.all([
        api.get('/dropdowns/roof_types').catch(() => ({ data: [] })),
        api.get('/condition-scripts'),
      ]);
      const types = (Array.isArray(rt.data) ? rt.data : (rt.data?.options || [])).filter(Boolean);
      setRoofTypes(types);
      setScripts(Array.isArray(sc.data) ? sc.data : []);
      if (types.length && !selectedRoofType) setSelectedRoofType(types[0]);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, selectedRoofType]);

  useEffect(() => { load(); }, [load]);

  // Merge legacy single `roof_type` field with the new `roof_types[]` array so
  // older docs (imported from Access) still surface in the filter.
  const resolvedRoofTypes = useCallback((s) => {
    const arr = Array.isArray(s.roof_types) ? [...s.roof_types] : [];
    if (s.roof_type && !arr.includes(s.roof_type)) arr.push(s.roof_type);
    return arr;
  }, []);

  // Count comments per Roof Type for the active category (chip badges).
  const roofTypeCounts = useMemo(() => {
    const counts = {};
    const inCat = scripts.filter(s => (s.inspection_category || s.category) === selectedCategory);
    roofTypes.forEach(rt => {
      counts[rt] = inCat.filter(s => {
        const r = resolvedRoofTypes(s);
        return r.length === 0 || r.includes(rt); // generic OR matches
      }).length;
    });
    counts['__generic__'] = inCat.filter(s => resolvedRoofTypes(s).length === 0).length;
    return counts;
  }, [scripts, roofTypes, selectedCategory, resolvedRoofTypes]);

  // Count comments per Category for the selected Roof Type (sidebar badges).
  const categoryCounts = useMemo(() => {
    const counts = {};
    CATEGORIES.forEach(c => {
      counts[c] = scripts.filter(s => {
        const inCat = (s.inspection_category || s.category) === c;
        if (!inCat) return false;
        if (!selectedRoofType) return true;
        const r = resolvedRoofTypes(s);
        return r.length === 0 || r.includes(selectedRoofType);
      }).length;
    });
    return counts;
  }, [scripts, selectedRoofType, resolvedRoofTypes]);

  // Comments visible in the main pane.
  const visibleScripts = useMemo(() => {
    return scripts.filter(s => {
      if ((s.inspection_category || s.category) !== selectedCategory) return false;
      if (!selectedRoofType) return true;
      const r = resolvedRoofTypes(s);
      return r.length === 0 || r.includes(selectedRoofType);
    }).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }, [scripts, selectedCategory, selectedRoofType, resolvedRoofTypes]);

  const beginEdit = (s) => {
    setEditingId(s._id);
    setEditForm({
      name: s.name || '',
      body: s.body || s.body_text || '',
      roof_types: [...resolvedRoofTypes(s)],
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const toggleRoofTypeOnEdit = (rt) => {
    setEditForm(prev => {
      const set = new Set(prev.roof_types || []);
      if (set.has(rt)) set.delete(rt); else set.add(rt);
      return { ...prev, roof_types: Array.from(set) };
    });
  };

  const saveEdit = async (s) => {
    setBusy(s._id);
    try {
      await api.put(`/condition-scripts/${s._id}`, {
        category: s.category || '',
        name: editForm.name,
        body: editForm.body,
        roof_type: '',
        roof_types: editForm.roof_types || [],
        inspection_category: s.inspection_category || '',
        recommendation: s.recommendation || '',
        sort_order: s.sort_order || 0,
        active: s.active !== false,
      });
      setScripts(prev => prev.map(x => x._id === s._id ? { ...x, name: editForm.name, body: editForm.body, roof_type: '', roof_types: editForm.roof_types || [] } : x));
      cancelEdit();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Save failed');
    }
    setBusy(null);
  };

  const deleteComment = async (s) => {
    if (!window.confirm(`Permanently delete comment "${s.name || '(unnamed)'}"?\n\nThis removes it from every Roof Type and every appraisal picker. Cannot be undone.`)) return;
    setBusy(s._id);
    try {
      await api.delete(`/condition-scripts/${s._id}`);
      setScripts(prev => prev.filter(x => x._id !== s._id));
      if (editingId === s._id) cancelEdit();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed');
    }
    setBusy(null);
  };

  const deleteRoofType = async (rt) => {
    const usage = scripts.reduce((n, s) => n + (resolvedRoofTypes(s).includes(rt) ? 1 : 0), 0);
    if (!window.confirm(`Permanently delete Roof Type "${rt}"?\n\nIt is currently linked to ${usage} comment${usage === 1 ? '' : 's'}. Those comments will become generic (apply to all roofs).\n\nThis also removes "${rt}" from every dropdown across the app. Cannot be undone.`)) return;
    setBusy('rt:' + rt);
    try {
      await api.delete(`/condition-scripts/roof-type/${encodeURIComponent(rt)}`);
      setRoofTypes(prev => prev.filter(x => x !== rt));
      setScripts(prev => prev.map(s => ({
        ...s,
        roof_type: s.roof_type === rt ? '' : s.roof_type,
        roof_types: (s.roof_types || []).filter(x => x !== rt),
      })));
      if (selectedRoofType === rt) {
        const next = roofTypes.find(x => x !== rt) || '';
        setSelectedRoofType(next);
      }
    } catch (e) {
      alert(e?.response?.data?.detail || 'Roof type delete failed');
    }
    setBusy(null);
  };

  return (
    <div data-testid="roof-conditions-page" className="space-y-4">
      {/* Header */}
      <div className="bg-white border border-[#E2E2DF] rounded-md p-6 relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[3px] bg-[#316536]" />
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-md bg-gradient-to-br from-[#316536]/15 to-[#316536]/5 flex items-center justify-center ring-1 ring-[#316536]/10 flex-shrink-0">
            <ClipboardList size={22} className="text-[#316536]" strokeWidth={2} />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-0.5">Config</p>
            <h1 className="text-2xl font-extrabold text-slate-900 font-[Manrope] tracking-tight">Roof Conditions</h1>
            <p className="text-sm text-slate-500 mt-1 max-w-3xl">
              See exactly which condition comments are available for each Roof Type during an appraisal. Click a comment to edit its description and toggle which Roof Types it applies to. Delete unwanted comments or whole Roof Types. Same underlying data as the Roof Appraisal picker.
            </p>
          </div>
        </div>
      </div>

      {/* Roof Type chips with deletion */}
      <div className="bg-white border border-[#E2E2DF] rounded-md p-4">
        <div className="flex items-center justify-between mb-3">
          <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Roof Type</label>
          <span className="text-[11px] text-slate-400">{roofTypes.length} types</span>
        </div>
        {loading ? (
          <div className="text-sm text-slate-400"><Loader2 size={14} className="inline animate-spin mr-2" /> Loading…</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {roofTypes.map(rt => {
              const active = selectedRoofType === rt;
              const ct = roofTypeCounts[rt] || 0;
              const isBusy = busy === 'rt:' + rt;
              return (
                <div key={rt} className={`inline-flex items-center rounded-md border transition-all overflow-hidden ${active ? 'border-[#D35400] ring-2 ring-[#D35400]/15' : 'border-[#E2E2DF] hover:border-slate-400'}`}>
                  <button
                    data-testid={`roof-type-btn-${rt}`}
                    onClick={() => setSelectedRoofType(rt)}
                    className={`flex items-center gap-2 px-3 py-1.5 text-sm ${active ? 'bg-[#D35400] text-white' : 'text-slate-700 hover:bg-slate-50'}`}
                  >
                    <span>{rt}</span>
                    {ct > 0 && <span className={`text-[10px] font-mono-num rounded-full px-1.5 py-0.5 ${active ? 'bg-white/20' : 'bg-slate-100 text-slate-500'}`}>{ct}</span>}
                  </button>
                  <button
                    data-testid={`delete-roof-type-${rt}`}
                    onClick={() => deleteRoofType(rt)}
                    title={`Delete Roof Type "${rt}" everywhere`}
                    disabled={isBusy}
                    className="px-2 py-1.5 border-l border-inherit text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-40"
                  >
                    {isBusy ? <Loader2 size={11} className="animate-spin" /> : <X size={11} />}
                  </button>
                </div>
              );
            })}
            {roofTypes.length === 0 && <p className="text-sm text-slate-400 italic">No roof types defined yet — add one from Settings → Dropdown Options.</p>}
          </div>
        )}
      </div>

      {selectedRoofType && (
        <div className="bg-white border border-[#E2E2DF] rounded-md overflow-hidden">
          <div className="flex">
            {/* Category sidebar */}
            <div className="w-48 border-r border-[#E2E2DF] bg-slate-50/40 flex-shrink-0">
              <div className="px-3 py-2 border-b border-[#E2E2DF] bg-white">
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Category</p>
              </div>
              {CATEGORIES.map(c => {
                const ct = categoryCounts[c] || 0;
                const active = selectedCategory === c;
                return (
                  <button
                    key={c}
                    data-testid={`category-${c}`}
                    onClick={() => { setSelectedCategory(c); cancelEdit(); }}
                    className={`w-full flex items-center justify-between px-3 py-2 text-left text-xs transition-all border-b border-[#E2E2DF] ${active ? 'bg-white text-[#D35400] font-semibold border-l-2 border-l-[#D35400]' : 'text-slate-600 hover:bg-white border-l-2 border-l-transparent'}`}
                  >
                    <span>{c}</span>
                    <div className="flex items-center gap-1">
                      {ct > 0 && <span className={`text-[9px] font-mono-num rounded-full px-1.5 py-0.5 ${active ? 'bg-[#D35400]/15 text-[#D35400]' : 'bg-slate-100 text-slate-500'}`}>{ct}</span>}
                      <ChevronRight size={11} className="text-slate-300" />
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Comments list */}
            <div className="flex-1 p-4 min-w-0">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-base font-semibold text-slate-800 font-[Manrope]">
                    {selectedCategory} comments
                    <span className="text-xs text-slate-400 font-normal ml-2">visible to "{selectedRoofType}" appraisals</span>
                  </h3>
                  <p className="text-[11px] text-slate-500 mt-0.5">Click any comment to edit. Generic comments (no Roof Type ticked) appear under every Roof Type.</p>
                </div>
                <span className="text-[11px] text-slate-400 font-mono-num">{visibleScripts.length} shown</span>
              </div>

              {visibleScripts.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-[#E2E2DF] rounded-md text-sm text-slate-400">
                  No {selectedCategory.toLowerCase()} comments are mapped to "{selectedRoofType}" yet.<br />
                  <span className="text-[11px] mt-1 inline-block">Add new comments via <strong>Condition Scripts</strong> in the sidebar.</span>
                </div>
              ) : (
                <div className="space-y-2">
                  {visibleScripts.map(s => {
                    const isEditing = editingId === s._id;
                    const isBusy = busy === s._id;
                    const r = resolvedRoofTypes(s);
                    const isGeneric = r.length === 0;
                    return (
                      <div
                        key={s._id}
                        data-testid={`comment-${s._id}`}
                        className={`border rounded-md transition-all ${isEditing ? 'border-[#D35400] ring-2 ring-[#D35400]/15' : 'border-[#E2E2DF] hover:border-slate-400'}`}
                      >
                        {!isEditing ? (
                          <button onClick={() => beginEdit(s)} className="w-full text-left p-3 flex items-start gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-bold text-sm text-slate-800">{s.name || '(unnamed)'}</p>
                                {isGeneric ? (
                                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">
                                    <AlertTriangle size={9} /> Generic — all roofs
                                  </span>
                                ) : (
                                  r.slice(0, 6).map(x => (
                                    <span key={x} className={`text-[10px] rounded px-1.5 py-0.5 border ${x === selectedRoofType ? 'bg-emerald-50 text-emerald-700 border-emerald-200 font-bold' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>{x}</span>
                                  ))
                                )}
                                {r.length > 6 && <span className="text-[10px] text-slate-400">+{r.length - 6} more</span>}
                              </div>
                              <p className="text-[12px] text-slate-500 leading-snug mt-1 line-clamp-2">{(s.body || s.body_text || '').slice(0, 280)}{(s.body || s.body_text || '').length > 280 ? '…' : ''}</p>
                            </div>
                            <span className="flex-shrink-0 text-slate-300"><Edit3 size={13} /></span>
                          </button>
                        ) : (
                          <div className="p-3 space-y-3 bg-white">
                            <div>
                              <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-1">Short name</label>
                              <input
                                data-testid={`edit-name-${s._id}`}
                                value={editForm.name}
                                onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                                className="w-full px-3 py-1.5 border border-[#E2E2DF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-1">Body text (used in PDFs)</label>
                              <textarea
                                data-testid={`edit-body-${s._id}`}
                                rows={4}
                                value={editForm.body}
                                onChange={e => setEditForm(prev => ({ ...prev, body: e.target.value }))}
                                className="w-full px-3 py-1.5 border border-[#E2E2DF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400] leading-relaxed"
                              />
                            </div>
                            <div>
                              <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-1">Applies to Roof Types
                                <span className="font-normal normal-case ml-1 text-slate-400">(no ticks = generic, shows under every roof)</span>
                              </label>
                              <div className="flex flex-wrap gap-1.5 mt-1">
                                {roofTypes.map(rt => {
                                  const checked = (editForm.roof_types || []).includes(rt);
                                  return (
                                    <button
                                      key={rt}
                                      type="button"
                                      data-testid={`edit-rt-toggle-${s._id}-${rt}`}
                                      onClick={() => toggleRoofTypeOnEdit(rt)}
                                      className={`flex items-center gap-1 text-[11px] px-2 py-1 rounded border transition-all ${checked ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                                    >
                                      {checked && <Check size={10} />}
                                      {rt}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                            <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#E2E2DF]">
                              <button
                                data-testid={`delete-comment-${s._id}`}
                                onClick={() => deleteComment(s)}
                                disabled={isBusy}
                                className="flex items-center gap-1 text-[11px] font-semibold text-rose-600 border border-rose-200 hover:bg-rose-50 px-2.5 py-1 rounded-md disabled:opacity-50"
                              >
                                <Trash2 size={11} /> Delete comment
                              </button>
                              <div className="flex-1" />
                              <button
                                onClick={cancelEdit}
                                className="text-[11px] text-slate-500 hover:text-slate-700 px-2.5 py-1"
                              >
                                Cancel
                              </button>
                              <button
                                data-testid={`save-comment-${s._id}`}
                                onClick={() => saveEdit(s)}
                                disabled={isBusy}
                                className="flex items-center gap-1 text-[11px] font-bold text-white bg-[#D35400] hover:bg-[#A04000] px-3 py-1 rounded-md disabled:opacity-50"
                              >
                                {isBusy ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />} Save
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <p className="text-[11px] text-slate-500">
        <strong>Tip:</strong> The same comments are managed by the <strong>Comment ↔ Roof Matrix</strong> (grid layout, faster for bulk editing) and surfaced by the appraisal flow. Changes here propagate everywhere.
      </p>
    </div>
  );
}
